import React, { useMemo } from "react";

import { Box, Chip, Grid, MenuItem, SelectChangeEvent } from "@mui/material";
import { useTranslation } from "react-i18next";

import { Input, Select } from "../../../../../../../../components/Form";
import AutoSuggest from "../../../../../../../../components/Form/AutoSuggest";
import SkeletonComponent from "../../../../../../../../components/Generic/Skeleton";
import { useAppState } from "../../../../../../../../context/StateContext";
import useActionCustomers from "../../../../../../../../hooks/useActionCustomer";
import { Customer } from "../../../../../../../../models/customer";
import { ProductStatus } from "../../../../../../../../util/enums";
import { useProductContext } from "../../../../context/ProductContext";
import { ProductSectionTitle } from "../../../components/SectionTitle";
import useActionProduct from "../../../hooks/useAction";
import { ProductSectionsProps } from "../../../types";
import { ProductDetailsFields } from "../../../util";
import useStyles from "./styles";

interface StatusSectionProps extends ProductSectionsProps {}

export const ProductDetailsSection = ({ generateBaseFieldProps }: StatusSectionProps) => {
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();
  const { product, handleUpdateProduct } = useProductContext();
  const { getAllCurrencies } = useActionProduct();
  const { getAllCustomers } = useActionCustomers();
  const classes = useStyles();

  const { data: currenciesData, isLoading: isLoadingCurrencies } = getAllCurrencies();
  const { data: customerDataOptions, isFetching: loadingCustomers } = getAllCustomers({
    companyId: currentUser?.companyId,
  });

  const isEditOrView = useMemo(() => !!product?.id, [product?.id]);
  const customerOptions = useMemo(() => {
    return customerDataOptions?.sort((a, b) => (a.fullName || "").localeCompare(b.fullName || ""));
  }, [customerDataOptions]);
  const customerSelected = useMemo(() => {
    return customerOptions?.find((customer) => product?.customerId === customer.id);
  }, [customerOptions, product?.customerId]);
  const projectNames = useMemo(() => {
    if (product?.purchaseOrders?.length) {
      const projectNames = product.purchaseOrders.map((order) => order?.project?.name || "");

      return Array.from(new Set(projectNames)).sort((a, b) => a.localeCompare(b));
    }
  }, [product?.purchaseOrders]);

  const renderChipValueProjets = () => {
    return (
      <Box className={classes.chipBoxProject}>
        {projectNames?.map((name: string) => {
          return <Chip key={name} label={name} className={classes.chipBranch} />;
        })}
      </Box>
    );
  };

  return (
    <Grid container spacing={2}>
      <ProductSectionTitle>{translate("general")}</ProductSectionTitle>
      <Grid container item xs={12} md={12} spacing={2}>
        <Grid item xs={12} sm={4}>
          <SkeletonComponent isLoading={false}>
            <Input
              {...generateBaseFieldProps(ProductDetailsFields.SKU)}
              required
              disabled={isEditOrView}
              value={product?.sku || ""}
              onChange={({ target }) =>
                handleUpdateProduct?.(ProductDetailsFields.SKU, target.value)
              }
            />
          </SkeletonComponent>
        </Grid>
        <Grid item xs={12} md={4}>
          <Input
            {...generateBaseFieldProps(ProductDetailsFields.DESCRIPTION)}
            required
            label={translate("productName")}
            value={product?.description || ""}
            onChange={({ target }) =>
              handleUpdateProduct?.(ProductDetailsFields.DESCRIPTION, target.value)
            }
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Select
            {...generateBaseFieldProps(ProductDetailsFields.STATUS)}
            required
            value={product?.status}
            onChange={(event: SelectChangeEvent<any>) =>
              handleUpdateProduct?.(ProductDetailsFields.STATUS, event.target.value)
            }
            displayEmpty
          >
            {Object.values(ProductStatus).map((option) => (
              <MenuItem value={option} key={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </Grid>
        <Grid item xs={12} sm={4}>
          <SkeletonComponent isLoading={loadingCustomers}>
            <AutoSuggest
              {...generateBaseFieldProps(ProductDetailsFields.CUSTOMER_ID)}
              data-cy="autosuggest-consignee"
              required
              name="consignee-id"
              label={translate("consignee")}
              placeholder={translate("searchByNameOrEmail")}
              inputValue={customerSelected?.fullName || ""}
              options={customerOptions || []}
              getOptionSelected={(option?: Customer, value?: Customer) => option?.id === value?.id}
              getOptionLabel={(customer: Customer) => customer.fullName || ""}
              onChange={(customer: Customer) =>
                handleUpdateProduct?.(ProductDetailsFields.CUSTOMER_ID, customer?.id)
              }
              disabled={isEditOrView}
            />
          </SkeletonComponent>
        </Grid>
        <Grid item xs={12} md={4}>
          <Input
            {...generateBaseFieldProps(ProductDetailsFields.STYLE)}
            label={translate("styleCode")}
            value={product?.style || ""}
            onChange={({ target }) =>
              handleUpdateProduct?.(ProductDetailsFields.STYLE, target.value)
            }
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <Input
            {...generateBaseFieldProps(ProductDetailsFields.STOCK)}
            disabled
            label={translate("companyCurrentStock")}
            value={product?.currentStock || ""}
            onChange={({ target }) =>
              handleUpdateProduct?.(ProductDetailsFields.STOCK, target.value)
            }
          />
        </Grid>
        {!!projectNames && (
          <Grid item xs={12} sm={12}>
            <Select
              {...generateBaseFieldProps(ProductDetailsFields.PROJECT)}
              disabled
              multiple
              renderValue={() => renderChipValueProjets()}
              value={projectNames}
            >
              {projectNames.map((name) => (
                <MenuItem data-cy={name} value={name} key={name}>
                  {name}
                </MenuItem>
              ))}
            </Select>
          </Grid>
        )}
        <Grid item xs={12} md={4}>
          <Input
            {...generateBaseFieldProps(ProductDetailsFields.IN_TRANSIT_QUANTITY)}
            disabled
            label={translate("unitsInTransit")}
            placeholder={translate("unitsInTransit")}
            value={product?.cartonQuantities?.inTransit || "0"}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <Input
            {...generateBaseFieldProps(ProductDetailsFields.CARTONS_TO_DESPATCH)}
            disabled
            label={translate("cartonsToDespatch")}
            value={product?.cartonQuantities?.forDespatch || "0"}
          />
        </Grid>
      </Grid>
      <ProductSectionTitle>{translate("features")}</ProductSectionTitle>
      <Grid container item xs={12} md={12} spacing={2}>
        <Grid item xs={12} md={4}>
          <Input
            {...generateBaseFieldProps(ProductDetailsFields.DETAILS)}
            required
            label={translate("details")}
            value={product?.details || ""}
            onChange={({ target }) =>
              handleUpdateProduct?.(ProductDetailsFields.DETAILS, target.value)
            }
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <Input
            {...generateBaseFieldProps(ProductDetailsFields.LINING)}
            value={product?.lining || ""}
            onChange={({ target }) =>
              handleUpdateProduct?.(ProductDetailsFields.LINING, target.value)
            }
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <Input
            {...generateBaseFieldProps(ProductDetailsFields.FABRIC)}
            value={product?.fabric || ""}
            onChange={({ target }) =>
              handleUpdateProduct?.(ProductDetailsFields.FABRIC, target.value)
            }
          />
        </Grid>
      </Grid>
      <Grid container item xs={12} md={12} spacing={2}>
        <Grid item xs={12} md={4}>
          <Input
            {...generateBaseFieldProps(ProductDetailsFields.CONSTRUCTION)}
            value={product?.construction || ""}
            onChange={({ target }) =>
              handleUpdateProduct?.(ProductDetailsFields.CONSTRUCTION, target.value)
            }
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <Input
            {...generateBaseFieldProps(ProductDetailsFields.COMPOSITION)}
            value={product?.composition || ""}
            onChange={({ target }) =>
              handleUpdateProduct?.(ProductDetailsFields.COMPOSITION, target.value)
            }
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <Input
            {...generateBaseFieldProps(ProductDetailsFields.GENDER)}
            value={product?.gender || ""}
            onChange={({ target }) =>
              handleUpdateProduct?.(ProductDetailsFields.GENDER, target.value)
            }
          />
        </Grid>
      </Grid>

      <ProductSectionTitle>{translate("price")}</ProductSectionTitle>
      <Grid container item xs={12} md={12} spacing={2}>
        <Grid item xs={12} md={4}>
          <SkeletonComponent isLoading={isLoadingCurrencies}>
            <Select
              {...generateBaseFieldProps(ProductDetailsFields.CURRENCY)}
              required
              defaultValue=""
              displayEmpty
              label={translate("currency")}
              value={product?.currencyId || ""}
              onChange={({ target }) =>
                handleUpdateProduct?.(ProductDetailsFields.CURRENCY, target.value)
              }
              placeholder={translate("currency")}
            >
              {currenciesData &&
                currenciesData?.map((currency) => (
                  <MenuItem key={currency.id} value={currency.id}>
                    {currency.code}
                  </MenuItem>
                ))}
            </Select>
          </SkeletonComponent>
        </Grid>
        <Grid item xs={12} md={4}>
          <Input
            {...generateBaseFieldProps(ProductDetailsFields.UNIT_PRICE)}
            required
            type="number"
            min={0}
            value={product?.unitPrice || ""}
            onChange={({ target }) =>
              handleUpdateProduct?.(ProductDetailsFields.UNIT_PRICE, target.value)
            }
          />
        </Grid>
      </Grid>
    </Grid>
  );
};
